// Asset URL utility for CDN/local fallback

interface AssetManifest {
  [key: string]: string;
}

const assetManifest: AssetManifest | null = null;
let assetBaseUrl: string | null = null;
let manifestLoadAttempted = false;

/**
 * Initialize asset configuration
 */
export function initAssets() {
  // Only try to load manifest once
  if (manifestLoadAttempted) {
    return;
  }

  manifestLoadAttempted = true;

  // Skip asset manifest loading for now to prevent application getting stuck
  // The application works fine with local assets
  if (process.env.NODE_ENV === "development") {
    console.warn("🔧 Asset manifest loading disabled - using local assets");
  }

  // Set asset base URL - ensure HTTPS and no port for production
  let baseUrl = import.meta.env.VITE_ASSET_BASE_URL || "https://tellerwand.com";
  // Remove any port numbers and ensure HTTPS for production assets
  baseUrl = baseUrl.replace(/:\d+/, "").replace(/^http:/, "https:");
  assetBaseUrl = baseUrl;
}

/**
 * Get asset URL from CDN or local fallback
 */
export function getAssetUrl(localPath: string): string {
  // If we have a manifest and the path exists, use CDN URL
  if (assetManifest?.[localPath]) {
    return assetManifest[localPath];
  }

  // If we have a base URL, construct CDN URL
  if (assetBaseUrl) {
    // Remove leading slash if present
    const cleanPath = localPath.startsWith("/") ? localPath.slice(1) : localPath;
    return `${assetBaseUrl}/${cleanPath}`;
  }

  // Fallback to local path
  return localPath;
}

/**
 * Get image URL with proper CDN handling
 * @deprecated Use getAssetUrl directly instead
 */
export function _getImageUrl(imagePath: string): string {
  // Handle different image path formats
  let normalizedPath = imagePath;

  // If it's already a full URL, return as-is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it's a relative path starting with @/, convert to proper path
  if (imagePath.startsWith("@/")) {
    normalizedPath = imagePath.replace("@/", "src/");
  }

  // If it's a public path, keep as-is
  if (imagePath.startsWith("/")) {
    normalizedPath = imagePath.slice(1); // Remove leading slash
  }

  return getAssetUrl(normalizedPath);
}

/**
 * Get favicon URL
 */
export function getFaviconUrl(): string {
  return getAssetUrl("favicon.ico");
}

/**
 * Get logo URL
 */
export function getLogoUrl(variant: "default" | "clean" = "default"): string {
  const logoPath = variant === "clean" ? "tellerwand-logo-clean.png" : "tellerwand-logo.png";
  return getAssetUrl(logoPath);
}

/**
 * Check if CDN is available
 */
export function isCDNAvailable(): boolean {
  return assetBaseUrl !== null || assetManifest !== null;
}

/**
 * Get asset base URL
 */
export function getAssetBaseUrl(): string {
  return assetBaseUrl || "";
}

// Initialize assets when module is loaded
if (typeof window !== "undefined") {
  initAssets();
}
